import React from 'react';
import { FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
      </div>

      {/* Content Container */}
      <div className="relative max-w-4xl mx-auto px-4 py-12">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-400/30 mb-4">
            <FileText className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            Terms of <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">Service</span>
          </h1>
          <p className="text-gray-300">Last Updated: June 19, 2026</p>
        </div>

        {/* Content Card */}
        <div className="backdrop-blur-xl bg-gradient-to-br from-slate-900/80 via-purple-900/80 to-slate-900/80 rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
          <div className="prose prose-invert prose-cyan max-w-none">

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms of Service ("Terms") govern your interaction with the SMS, MMS, and automated phone (AI voice agent) services made available through the EXITIUM Labs platform on behalf of the business you have chosen to communicate with ("Business"). By opting in to receive messages or by interacting with the AI phone agent, you agree to these Terms. If you do not agree, do not opt in and do not continue with automated calls.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. Our Role as a Platform</h2>
              <p className="text-gray-300 leading-relaxed">
                EXITIUM Labs provides communications technology to Businesses. When you receive a message or call, the Business is responsible for the underlying relationship with you (including obtaining your consent and the content of communications), and EXITIUM acts as the technology provider that delivers those communications. Your separate relationship with the Business may be governed by the Business's own terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. Description of Service</h2>
              <div className="text-gray-300 leading-relaxed">
                <p className="mb-3">Through the EXITIUM platform, a Business may:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Send customer satisfaction surveys</li>
                  <li>Collect feedback ratings (1-5 scale)</li>
                  <li>Send service updates and notifications</li>
                  <li>Place or receive calls using an automated AI voice agent</li>
                  <li>Communicate with customers who have opted in</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. Opt-In and Consent</h2>
              <div className="text-gray-300 leading-relaxed space-y-3">
                <p>By providing your phone number and checking the consent box on an opt-in form, you:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Expressly consent to receive SMS/MMS messages from the Business you opted in with</li>
                  <li>Acknowledge that consent is not a condition of purchase</li>
                  <li>Understand that message and data rates may apply</li>
                  <li>Agree that message frequency will vary</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. AI Voice Agent &amp; Call Recording</h2>
              <div className="text-gray-300 leading-relaxed space-y-3">
                <p>Some calls are handled by an automated AI voice agent. When this happens:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>You will be told at the start of the call that you are speaking with an automated/AI system</li>
                  <li>If the call is recorded, you will be notified at the start of the call</li>
                  <li>If you do not consent to recording or to speaking with an AI system, you may end the call or, where available, ask to speak with a person</li>
                  <li>Call audio and transcripts may be processed by our service providers solely to operate the service</li>
                </ul>
                <p className="mt-3">Responses generated by the AI agent may contain errors and are not a substitute for confirmation from the Business.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">6. Opt-Out and Cancellation</h2>
              <div className="text-gray-300 leading-relaxed">
                <p className="mb-3">You may opt out of receiving messages at any time by:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-white">Texting STOP:</strong> Reply with STOP to any message to immediately unsubscribe</li>
                  <li><strong className="text-white">Contacting Us:</strong> Email support@exitiumlabs.com with your opt-out request</li>
                </ul>
                <p className="mt-3">After opting out, you will receive one final confirmation message, then no further messages.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">7. Message Frequency and Costs</h2>
              <div className="text-gray-300 leading-relaxed space-y-3">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Message frequency varies depending on the Business and your interactions</li>
                  <li>Typical frequency: 1-4 messages per month</li>
                  <li>Message and data rates may apply based on your mobile carrier plan</li>
                  <li>We do not charge you for the SMS service, but your carrier may</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">8. Supported Carriers</h2>
              <p className="text-gray-300 leading-relaxed">
                Our service is available for major U.S. carriers including AT&amp;T, Verizon, T-Mobile, and others. Carrier-specific messaging rates and policies may apply. Carriers are not liable for delayed or undelivered messages.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">9. Help and Support</h2>
              <div className="text-gray-300 leading-relaxed">
                <p className="mb-3">For assistance:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Reply HELP to any message for support information</li>
                  <li>Email: support@exitiumlabs.com</li>
                  <li>Visit: https://exitiumlabs.com</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">10. Acceptable Use</h2>
              <div className="text-gray-300 leading-relaxed">
                <p className="mb-3">You agree NOT to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide false or inaccurate information</li>
                  <li>Use the service for unlawful purposes</li>
                  <li>Attempt to disrupt or interfere with the service</li>
                  <li>Opt in on behalf of others without their consent</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">11. Service Availability</h2>
              <div className="text-gray-300 leading-relaxed space-y-3">
                <p>We strive to provide reliable service, but:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Message and call delivery is not guaranteed and may be delayed</li>
                  <li>Service may be interrupted for maintenance or technical issues</li>
                  <li>Carriers may block or filter messages</li>
                  <li>We are not responsible for carrier-related delivery failures</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">12. Privacy and Data</h2>
              <p className="text-gray-300 leading-relaxed">
                Your use of these services is also governed by our Privacy Policy and AI &amp; Automated Processing Disclosure. By using the service, you consent to our collection and use of your information as described there.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">13. Limitation of Liability</h2>
              <div className="text-gray-300 leading-relaxed">
                <p className="mb-3">To the maximum extent permitted by law:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>EXITIUM Labs is not liable for any indirect, incidental, or consequential damages</li>
                  <li>We are not responsible for charges from your mobile carrier</li>
                  <li>We are not liable for failed, delayed, or misdirected messages, or for errors in AI-generated responses</li>
                  <li>Our total liability to you shall not exceed $100</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">14. Dispute Resolution</h2>
              <p className="text-gray-300 leading-relaxed">
                Any disputes arising from these Terms shall be resolved through binding arbitration in accordance with the laws of the State of California, USA. You agree to waive any right to a jury trial or to participate in a class action, to the extent permitted by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">15. Changes to Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective upon posting. Your continued use of the service after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">16. Termination</h2>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to terminate or suspend access to the service at any time, for any reason, without notice. Upon termination, your right to receive messages will immediately cease.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">17. Entire Agreement</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms, together with our Privacy Policy and AI Disclosure, constitute the entire agreement between you and EXITIUM Labs regarding these consumer services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">18. Contact Information</h2>
              <div className="text-gray-300 leading-relaxed">
                <p className="mb-2">For questions about these Terms, contact us:</p>
                <div className="mt-4 p-4 bg-black/20 border border-white/10 rounded-xl">
                  <p><strong className="text-white">EXITIUM Labs</strong></p>
                  <p>Email: support@exitiumlabs.com</p>
                  <p>Website: https://exitiumlabs.com</p>
                </div>
              </div>
            </section>

          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-400 text-sm">
            © 2026 <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 font-semibold">EXITIUM Labs</span>. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;